import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { SugarBabyProfile, LifestyleInterestTag, ArrangementTypeTag, ArrangementFrequency, FinancialSupportType } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaUpload from './MediaUpload';
import { FileWithPath } from 'react-dropzone';

// --- Constants for Select/Checkbox Options (should match types/enums) ---
// Définition explicite des tableaux littéraux pour Zod
const lifestyleTags = ['studies', 'professional', 'hobbies', 'travel', 'gastronomy', 'artsCulture', 'luxury', 'sports', 'wellness'] as const;
const arrangementTypeTags = ['mentorship', 'financialSupport', 'experiences', 'networking', 'shortTerm', 'longTerm', 'platonic', 'discreet', 'public_limited', 'eventsCompanion', 'regularDates'] as const;
const arrangementFrequencyOptions = ['occasional', 'regular', 'negotiable'] as const;
const financialSupportOptions = ['studies', 'project', 'allowance', 'gifts', 'negotiable'] as const;

// --- Zod Schema Definition ---
const sugarBabyProfileSchema = z.object({
  pseudo: z.string().min(2, { message: "Pseudo must be at least 2 characters." }),
  location: z.object({
    city: z.string().min(1, { message: "City is required." }),
    country: z.string().min(1, { message: "Country is required." }),
  }),
  description: z.string().min(10, { message: "Description must be at least 10 characters." }),
  lifestyleInterests: z.array(z.enum(lifestyleTags)).min(1, { message: "Select at least one interest." }),
  arrangementExpectations: z.object({
    type: z.array(z.enum(arrangementTypeTags)).min(1, { message: "Select at least one arrangement type." }),
    frequency: z.enum(arrangementFrequencyOptions, { required_error: "Frequency is required." }),
    platoniqueAllowed: z.boolean().default(false),
    financialSupportDetails: z.array(z.enum(financialSupportOptions)).optional(),
  }),
  contribution: z.string().min(10, { message: "Contribution must be at least 10 characters." }),
  boundaries: z.string().min(10, { message: "Boundaries must be at least 10 characters." }),
  // TODO: Add fields for photos/videos later
});

type SugarBabyProfileFormData = z.infer<typeof sugarBabyProfileSchema>;

// --- Component Props Interface ---
interface SugarBabyProfileFormProps {
  onSubmit: (data: SugarBabyProfileFormData, filesToUpload: FileWithPath[]) => Promise<void>;
  initialData?: Partial<SugarBabyProfile>;
  isSubmitting?: boolean;
  initialMedia?: { photos?: { url: string; name: string }[], videos?: { url: string; name: string }[] };
}

// --- Form Component ---
const SugarBabyProfileForm: React.FC<SugarBabyProfileFormProps> = ({ onSubmit, initialData, isSubmitting, initialMedia }) => {
  const { t } = useTranslation(['sugar', 'forms', 'common']);
  const [photoFiles, setPhotoFiles] = useState<FileWithPath[]>([]);
  const [videoFiles, setVideoFiles] = useState<FileWithPath[]>([]);
  
  const { control, register, handleSubmit, formState: { errors } } = useForm<SugarBabyProfileFormData>({
    resolver: zodResolver(sugarBabyProfileSchema),
    defaultValues: { // Map initialData carefully to form structure
        pseudo: initialData?.pseudo || '',
        location: initialData?.location || { city: '', country: '' },
        description: initialData?.description || '',
        lifestyleInterests: initialData?.lifestyleInterests || [],
        arrangementExpectations: {
            type: initialData?.arrangementExpectations?.type || [],
            frequency: initialData?.arrangementExpectations?.frequency || undefined,
            platoniqueAllowed: initialData?.arrangementExpectations?.platoniqueAllowed || false,
            financialSupportDetails: initialData?.arrangementExpectations?.financialSupportDetails || [],
        },
        contribution: initialData?.contribution || '',
        boundaries: initialData?.boundaries || '',
    },
  });
  
  const renderCheckboxGroup = (fieldName: any, options: readonly string[]) => (
      <div className="space-y-2"> 
          {options.map((option) => (
              <Controller
                  key={option}
                  name={fieldName}
                  control={control}
                  render={({ field }) => {
                      const currentValues = field.value || [];
                      return (
                          <div className="flex items-center space-x-2">
                              <Checkbox
                                  id={`${fieldName}-${option}`}
                                  checked={currentValues.includes(option)}
                                  onCheckedChange={(checked) => {
                                      const newValue = checked
                                          ? [...currentValues, option]
                                          : currentValues.filter((v: string) => v !== option);
                                      field.onChange(newValue);
                                  }}
                              />
                              <Label htmlFor={`${fieldName}-${option}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                  {t(`sugar:tags.${fieldName.split('.')[0] === 'arrangementExpectations' ? 'arrangement' : fieldName.split('.')[1]}.${option}`, option)} 
                              </Label>
                         </div>
                      );
                  }}
              />
          ))}
          {errors[fieldName] && <p className="mt-1 text-xs text-red-600">{errors[fieldName]?.message}</p>}
      </div>
  );

  const handleFormSubmit = (formData: SugarBabyProfileFormData) => {
      const allFiles = [...photoFiles, ...videoFiles];
      onSubmit(formData, allFiles);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
      {/* Pseudo */}
      <div>
        <Label htmlFor="pseudo">{t('forms:label_pseudo')}</Label>
        <Input id="pseudo" {...register('pseudo')} />
        {errors.pseudo && <p className="mt-1 text-xs text-red-600">{errors.pseudo.message}</p>}
      </div>

      {/* Location */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
              <Label htmlFor="location.city">{t('forms:label_city')}</Label>
              <Input id="location.city" {...register('location.city')} />
              {errors.location?.city && <p className="mt-1 text-xs text-red-600">{errors.location.city.message}</p>}
          </div>
           <div>
              <Label htmlFor="location.country">{t('forms:label_country')}</Label>
              <Input id="location.country" {...register('location.country')} />
              {errors.location?.country && <p className="mt-1 text-xs text-red-600">{errors.location.country.message}</p>}
          </div>
      </div>

      {/* Description */}
       <div>
        <Label htmlFor="description">{t('forms:label_description')}</Label>
        <Textarea id="description" rows={5} {...register('description')} />
        {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>}
      </div>
      
      {/* Lifestyle Interests (Checkboxes) */}
       <div>
        <Label>{t('forms:label_lifestyleInterests')}</Label>
        {renderCheckboxGroup('lifestyleInterests', lifestyleTags)}
        {errors.lifestyleInterests && <p className="mt-1 text-xs text-red-600">{errors.lifestyleInterests.message}</p>}
      </div>

       {/* Arrangement Expectations */}
      <div className="space-y-4 p-4 border rounded-md">
          <h4 className="font-medium">{t('forms:section_arrangementExpectations')}</h4>
          {/* Arrangement Type (Checkboxes) */}
          <div>
            <Label>{t('forms:label_arrangementType')}</Label>
            {renderCheckboxGroup('arrangementExpectations.type', arrangementTypeTags)}
            {errors.arrangementExpectations?.type && <p className="mt-1 text-xs text-red-600">{(errors.arrangementExpectations.type as { message: string }).message}</p>}
          </div>
          
           {/* Arrangement Frequency (Select) */}
           <div>
              <Label htmlFor="arrangementExpectations.frequency">{t('forms:label_arrangementFrequency')}</Label>
               <Controller
                  name="arrangementExpectations.frequency"
                  control={control}
                  render={({ field }) => (
                     <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectTrigger id="arrangementExpectations.frequency">
                            <SelectValue placeholder={t('forms:placeholder_select')} />
                        </SelectTrigger>
                        <SelectContent>
                        {arrangementFrequencyOptions.map(option => (
                            <SelectItem key={option} value={option}>{t(`sugar:tags.frequency.${option}`, option)}</SelectItem>
                        ))}
                        </SelectContent>
                    </Select>
                  )}
                />
              {errors.arrangementExpectations?.frequency && <p className="mt-1 text-xs text-red-600">{errors.arrangementExpectations.frequency.message}</p>}
            </div>

           {/* Platonic Allowed (Checkbox) */}
           <div className="flex items-center space-x-2">
                 <Controller
                  name="arrangementExpectations.platoniqueAllowed"
                  control={control}
                  render={({ field }) => (
                    <Checkbox 
                        id="arrangementExpectations.platoniqueAllowed"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                    />
                  )}
                 />
                 <Label htmlFor="arrangementExpectations.platoniqueAllowed">{t('forms:label_platonicAllowed')}</Label>
                 {errors.arrangementExpectations?.platoniqueAllowed && <p className="ml-2 mt-1 text-xs text-red-600">{errors.arrangementExpectations.platoniqueAllowed.message}</p>} 
           </div>
           
           {/* Financial Support Details (Checkboxes) */}
          <div>
            <Label>{t('forms:label_financialSupportDetails')}</Label>
            {renderCheckboxGroup('arrangementExpectations.financialSupportDetails', financialSupportOptions)}
            {errors.arrangementExpectations?.financialSupportDetails && <p className="mt-1 text-xs text-red-600">{errors.arrangementExpectations.financialSupportDetails.message}</p>}
          </div>
      </div>

      {/* Contribution */}
      <div>
        <Label htmlFor="contribution">{t('forms:label_contribution')}</Label>
        <Textarea id="contribution" rows={3} {...register('contribution')} />
        {errors.contribution && <p className="mt-1 text-xs text-red-600">{errors.contribution.message}</p>}
      </div>

      {/* Boundaries */}
      <div>
        <Label htmlFor="boundaries">{t('forms:label_boundaries')}</Label>
        <Textarea id="boundaries" rows={3} {...register('boundaries')} />
        {errors.boundaries && <p className="mt-1 text-xs text-red-600">{errors.boundaries.message}</p>}
      </div>
      
      {/* --- Media Upload Section --- */}
      <div className="space-y-4 p-4 border rounded-md">
          <h4 className="font-medium">{t('forms:section_photos')}</h4>
          <MediaUpload 
            onFilesChange={setPhotoFiles} 
            accept={{ 'image/*': ['.png', '.jpeg', '.jpg'] }}
            maxFiles={5}
            initialFiles={initialMedia?.photos}
          />
      </div>
      
       <div className="space-y-4 p-4 border rounded-md">
          <h4 className="font-medium">{t('forms:section_videos')}</h4>
          <MediaUpload 
            onFilesChange={setVideoFiles} 
            accept={{ 'video/*': ['.mp4', '.mov'] }} 
            maxFiles={2}
             initialFiles={initialMedia?.videos}
          />
      </div>
      {/* --- End Media Upload Section --- */}
      
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('common:submitting') : t('common:save_changes')}
      </Button>
    </form>
  );
};

export default SugarBabyProfileForm; 